import React from 'react';
import type { Bot } from '@/types/bot';
import { useForexRates } from '@/contexts/ForexRatesContext';
import './StrategyCard.css';

interface StrategyCardProps {
  bot: Bot;
}

function getCurrency(market: string): { symbol: string; code: string } {
  switch (market) {
    case 'India': return { symbol: '\u20B9', code: 'INR' };
    case 'US': return { symbol: '$', code: 'USD' };
    case 'UK': return { symbol: '\u00A3', code: 'GBP' };
    default: return { symbol: '$', code: 'USD' };
  }
}

function formatPnl(value: number, currencySymbol: string): string {
  const abs = Math.abs(value);
  const formatted = abs.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return value >= 0 ? `+${currencySymbol}${formatted}` : `-${currencySymbol}${formatted}`;
}

function formatAud(value: number, market: string, rates: Record<string, number>): string {
  const rate = rates[market] || 1;
  const aud = value * rate;
  const abs = Math.abs(aud);
  const formatted = abs.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return aud >= 0 ? `A$${formatted}` : `-A$${formatted}`;
}

function formatCount(value: number): string {
  return value.toLocaleString('en-US');
}

export const StrategyCard: React.FC<StrategyCardProps> = ({ bot }) => {
  const { rates } = useForexRates();
  const performance = bot.performance || { todayPnl: 0, totalPnl: 0, winRate: 0, totalTrades: 0, activePositions: 0, fills: 0 };
  const isPositive = performance.todayPnl >= 0;
  const isStopped = bot.status === 'stopped' || bot.status === 'paused';
  const currency = getCurrency(bot.market);

  return (
    <div className={`strategy-card ${isStopped ? 'strategy-card--stopped' : ''}`}>
      <div className="strategy-card__top">
        <span className="strategy-card__name">{bot.name}</span>
        <span className="strategy-card__market">[{bot.market}]</span>
        {isStopped && <span className="strategy-card__status-badge">STOPPED</span>}
      </div>

      <div className="strategy-card__pnl-row">
        <div
          className={`strategy-card__pnl ${
            isPositive ? 'strategy-card__pnl--positive' : 'strategy-card__pnl--negative'
          }`}
        >
          {formatPnl(performance.todayPnl, currency.symbol)}
        </div>
        <div className="strategy-card__aud">
          {formatAud(performance.todayPnl, bot.market, rates)}
        </div>
      </div>

      <div className="strategy-card__subtitle">
        {formatCount(performance.totalTrades)} ORDERS &middot; {formatCount(performance.fills)} FILLS
      </div>

      <div className="strategy-card__metrics">
        <div className="strategy-card__metric">
          <span className="strategy-card__metric-label">Positions</span>
          <span className="strategy-card__metric-value">{performance.activePositions}</span>
        </div>
        <div className="strategy-card__metric">
          <span className="strategy-card__metric-label">Fills</span>
          <span className="strategy-card__metric-value">{formatCount(performance.fills)}</span>
        </div>
        <div className="strategy-card__metric">
          <span className="strategy-card__metric-label">Win Rate</span>
          <span className="strategy-card__metric-value">{performance.winRate}%</span>
        </div>
      </div>

      <div
        className={`strategy-card__bar ${
          isPositive ? 'strategy-card__bar--positive' : 'strategy-card__bar--negative'
        }`}
      />
    </div>
  );
};
